/**
 * graphMailService.js
 *
 * Microsoft Graph API service layer for mail operations.
 *
 * All calls are proxied through the BPA backend at SERVER_URL so the Graph
 * access token never lives in the browser.  The backend holds the OAuth token,
 * refreshes it when needed, and calls graph.microsoft.com on behalf of the
 * authenticated user.
 *
 * The mailbox owner email is sent via the x-user-email request header so it
 * never appears in URLs or request bodies.
 *
 * Graph scopes required:
 *   Mail.Read  Mail.ReadWrite  Mail.Send
 */

import axios from 'axios';
import { SERVER_URL } from '../databucket';

// ---------------------------------------------------------------------------
// User email — set once at login via setServiceEmail(), read by all calls
// ---------------------------------------------------------------------------

let _userEmail = null;

export function setServiceEmail(email) {
  _userEmail = email;
}

function getEmail() {
  return _userEmail || localStorage.getItem('outlookUserEmail') || '';
}

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

const api = axios.create({
  baseURL: `${SERVER_URL}/api/mail`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Attach x-user-email header to every request — email never goes in the URL
api.interceptors.request.use((config) => {
  const email = getEmail();
  if (email) config.headers['x-user-email'] = email;
  return config;
});

// ---------------------------------------------------------------------------
// Response interceptor — normalise errors
// ---------------------------------------------------------------------------

api.interceptors.response.use(
  (res) => res.data,
  (err) => {
    const msg =
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      err?.message ||
      'Graph API error';
    return Promise.reject(new Error(msg));
  }
);

// ===========================================================================
// FOLDERS
// ===========================================================================

/**
 * Fetch all mail folders for the signed-in user.
 *
 * GET /api/mail/folders
 *
 * Returns:
 *   {
 *     folders: GraphFolder[],
 *     rootIds: string[],
 *     childrenByParent: Record<string, string[]>
 *   }
 */
export async function fetchFolders() {
  return api.get('/folders');
}

export async function fetchChildFolders(parentId) {
  return api.get(`/folders/${parentId}/children`);
}

export async function fetchFolderDelta(deltaToken) {
  const params = deltaToken ? { token: deltaToken } : {};
  return api.get('/folders/delta', { params });
}

// ===========================================================================
// THREADS  (conversationId grouping — done server-side)
// ===========================================================================

/**
 * Fetch threads (grouped messages) for a folder.
 *
 * GET /api/mail/folders/:folderId/threads?top=50&cursor=<skipToken>
 *
 * Returns:
 *   {
 *     threads:  GraphThread[],
 *     messages: GraphMessage[],
 *     cursor:   string | null,
 *     hasMore:  boolean
 *   }
 */
export async function fetchThreadsByFolder(folderId, { cursor = null, top = 50 } = {}) {
  const params = { top };
  if (cursor) params.cursor = cursor;
  return api.get(`/folders/${folderId}/threads`, { params });
}

// ===========================================================================
// SEARCH
// ===========================================================================

/**
 * Full-text search across all messages via Graph $search (KQL).
 *
 * GET /api/mail/search?q=&top=25&cursor=
 *
 * Pass `signal` (AbortSignal) to cancel in-flight requests when a new
 * search supersedes the current one.
 *
 * Returns: { messages: MappedMessage[], total: number, cursor: string|null }
 */
export async function searchMessages(query, { top = 25, cursor = null, signal } = {}) {
  const params = { q: query, top };
  if (cursor) params.cursor = cursor;
  return api.get('/search', { params, signal });
}

// ===========================================================================
// CONVERSATIONS
// ===========================================================================

/**
 * Fetch all messages in a conversation across all folders.
 *
 * GET /api/mail/conversations/:conversationId/messages
 *
 * Returns: { messages: MappedMessage[] }
 */
export async function fetchConversationMessages(conversationId) {
  return api.get(`/conversations/${conversationId}/messages`);
}

// ===========================================================================
// MESSAGES
// ===========================================================================

/**
 * Fetch full HTML body of a single message.
 *
 * GET /api/mail/messages/:messageId/body
 *
 * Returns:
 *   {
 *     content:           string,
 *     contentType:       'html' | 'text',
 *     blockedImageCount: number
 *   }
 */
export async function fetchMessageBody(messageId) {
  return api.get(`/messages/${messageId}/body`);
}

export async function fetchAttachments(messageId) {
  return api.get(`/messages/${messageId}/attachments`);
}

export async function addAttachmentToMessage(messageId, attachment) {
  return api.post(`/messages/${messageId}/attachments`, attachment);
}

export async function markMessageRead(messageId, isRead = true) {
  return api.patch(`/messages/${messageId}/read`, { isRead });
}

export async function moveMessage(messageId, destinationId) {
  return api.post(`/messages/${messageId}/move`, { destinationId });
}

export async function deleteMessage(messageId) {
  return api.delete(`/messages/${messageId}`);
}

export async function updateMessageFlag(messageId, flagStatus) {
  return api.patch(`/messages/${messageId}/flag`, { flagStatus });
}

// ===========================================================================
// SEND / REPLY / FORWARD
// ===========================================================================

/**
 * Send a new email.
 *
 * POST /api/mail/messages/send
 * Body: GraphSendPayload
 */
export async function sendMessage(payload) {
  return api.post('/messages/send', payload);
}

export async function replyToMessage(messageId, payload) {
  return api.post(`/messages/${messageId}/reply`, payload);
}

export async function replyAllToMessage(messageId, payload) {
  return api.post(`/messages/${messageId}/replyAll`, payload);
}

export async function forwardMessage(messageId, payload) {
  return api.post(`/messages/${messageId}/forward`, payload);
}

// ===========================================================================
// DRAFT MANAGEMENT
// ===========================================================================

/**
 * Create a draft message.
 *
 * POST /api/mail/messages/drafts
 */
export async function createDraft(payload) {
  return api.post('/messages/drafts', payload);
}

/**
 * Update an existing draft.
 *
 * PATCH /api/mail/messages/drafts/:messageId
 */
export async function updateDraft(messageId, payload) {
  return api.patch(`/messages/drafts/${messageId}`, payload);
}

/**
 * Send a saved draft.
 *
 * POST /api/mail/messages/drafts/:messageId/send
 */
export async function sendDraft(messageId) {
  return api.post(`/messages/drafts/${messageId}/send`);
}

/**
 * Delete a draft message (discard).
 *
 * DELETE /api/mail/messages/drafts/:messageId
 */
export async function deleteDraft(messageId) {
  return api.delete(`/messages/drafts/${messageId}`);
}

// ===========================================================================
// DELTA SYNC
// ===========================================================================

/**
 * Fetch message delta changes for a folder since last sync.
 *
 * GET /api/mail/folders/:folderId/delta?token=<deltaToken>
 *
 * Returns:
 *   {
 *     added:    GraphMessage[],
 *     modified: GraphMessage[],
 *     removed:  { id: string }[],
 *     deltaToken: string
 *   }
 */
export async function fetchMessageDelta(folderId, deltaToken) {
  const params = deltaToken ? { token: deltaToken } : {};
  return api.get(`/folders/${folderId}/delta`, { params });
}

// ===========================================================================
// TYPE MAPPERS  (Graph → Redux slice shapes)
// ===========================================================================

/**
 * Map a raw Graph folder object to the mailFoldersSlice Folder shape.
 *
 * @param {object} gFolder  — raw Graph mailFolder
 * @returns {import('../Slices/mailFoldersSlice').Folder}
 */
export function mapGraphFolder(gFolder) {
  return {
    id:               gFolder.id,
    displayName:      gFolder.displayName,
    wellKnownName:    gFolder.wellKnownName   || null,
    parentFolderId:   gFolder.parentFolderId  || null,
    childFolderCount: gFolder.childFolderCount || 0,
    totalItemCount:   gFolder.totalItemCount  || 0,
    unreadItemCount:  gFolder.unreadItemCount || 0,
  };
}

/**
 * Map a raw Graph message object to the mailMessagesSlice Message shape.
 *
 * @param {object} gMsg  — raw Graph message
 * @returns {import('../Slices/mailMessagesSlice').Message}
 */
export function mapGraphMessage(gMsg) {
  return {
    id:               gMsg.id,
    conversationId:   gMsg.conversationId,
    folderId:         gMsg.parentFolderId || null,
    from: {
      email: gMsg.from?.emailAddress?.address || '',
      name:  gMsg.from?.emailAddress?.name    || '',
    },
    toRecipients: (gMsg.toRecipients || []).map((r) => ({
      email: r.emailAddress?.address || '',
      name:  r.emailAddress?.name    || '',
    })),
    ccRecipients: (gMsg.ccRecipients || []).map((r) => ({
      email: r.emailAddress?.address || '',
      name:  r.emailAddress?.name    || '',
    })),
    bccRecipients: (gMsg.bccRecipients || []).map((r) => ({
      email: r.emailAddress?.address || '',
      name:  r.emailAddress?.name    || '',
    })),
    subject:            gMsg.subject          || '(No subject)',
    bodyPreview:        gMsg.bodyPreview       || '',
    body: {
      content:           '',
      contentType:       gMsg.body?.contentType || 'html',
      loaded:            false,
      blockedImageCount: 0,
      imagesUnblocked:   false,
    },
    bodyStatus:         'not-loaded',
    hasAttachments:     gMsg.hasAttachments    || false,
    attachments:        [],
    attachmentsLoaded:  false,
    receivedDateTime:   gMsg.receivedDateTime  || null,
    sentDateTime:       gMsg.sentDateTime      || null,
    isRead:             gMsg.isRead            ?? true,
    isDraft:            gMsg.isDraft           || false,
    flag: {
      flagStatus: gMsg.flag?.flagStatus || 'notFlagged',
    },
    internetMessageId:  gMsg.internetMessageId  || null,
    conversationIndex:  gMsg.conversationIndex  || '',
    importance:         gMsg.importance         || 'normal',
    categories:         gMsg.categories         || [],
  };
}

/**
 * Build a Thread summary from an array of messages that share a conversationId.
 *
 * @param {string}   conversationId
 * @param {object[]} messages         — already mapped by mapGraphMessage
 * @param {string}   folderId
 * @returns {import('../Slices/mailThreadsSlice').Thread}
 */
export function buildThreadFromMessages(conversationId, messages, folderId) {
  const sorted = [...messages].sort((a, b) => {
    const ta = a.receivedDateTime || a.sentDateTime || '';
    const tb = b.receivedDateTime || b.sentDateTime || '';
    return ta < tb ? -1 : ta > tb ? 1 : 0;
  });

  const latest      = sorted[sorted.length - 1];
  const unreadMsgs  = sorted.filter((m) => !m.isRead && !m.isDraft);
  const uniqueNames = [...new Set(
    sorted.map((m) => m.from?.name || m.from?.email).filter(Boolean)
  )];

  return {
    conversationId,
    folderId,
    subject:          latest.subject,
    bodyPreview:      latest.bodyPreview,
    latestReceivedAt: latest.receivedDateTime || latest.sentDateTime || null,
    latestSenderId:   latest.from?.email || '',
    latestSenderName: latest.from?.name || latest.from?.email || '',
    participants:     uniqueNames,
    hasUnread:        unreadMsgs.length > 0,
    unreadCount:      unreadMsgs.length,
    messageCount:     sorted.length,
    messageIds:       sorted.map((m) => m.id),
    isFlagged:        sorted.some((m) => m.flag?.flagStatus === 'flagged'),
    categories:       latest.categories || [],
    hasAttachments:   sorted.some((m) => m.hasAttachments),
  };
}

/**
 * Group an array of mapped messages by conversationId and build Thread objects.
 *
 * @param {object[]} messages  — mapped by mapGraphMessage
 * @param {string}   folderId
 * @returns {{ threads: Thread[], messages: Message[] }}
 */
export function groupMessagesIntoThreads(messages, folderId) {
  const byConv = {};
  messages.forEach((msg) => {
    const cid = msg.conversationId;
    if (!byConv[cid]) byConv[cid] = [];
    byConv[cid].push(msg);
  });

  const threads = Object.entries(byConv).map(([cid, msgs]) =>
    buildThreadFromMessages(cid, msgs, folderId)
  );

  threads.sort((a, b) => {
    const ta = a.latestReceivedAt || '';
    const tb = b.latestReceivedAt || '';
    return ta < tb ? 1 : ta > tb ? -1 : 0;
  });

  return { threads, messages };
}
